// Import necessary components and utilities
import { CloseIcon } from '@chakra-ui/icons';
import { Badge, Flex, Text, Tooltip, Button } from '@chakra-ui/react';
import { format, parseJSON } from 'date-fns';
import { FaThList } from 'react-icons/fa';
import { MdAssignmentTurnedIn } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import {
  getLatestFileSubmissionSession,
  getSubQuestById,
} from 'store/modules/subquest/subquest-action';
import { subQuestReportActions } from 'store/modules/subQuestReport/subQuest-report-slice';
import {
  FILE_SUBMISSION_ID,
  INTERACTIVE_ID,
  QUIZ_ID,
} from 'utils/constants/subQuestType';
import { resultTextColors } from 'utils/constants/result';

export const RemoveProgressionButton = () => {
  const dispatch = useDispatch();

  // Handle click event for the button
  const handleClickDeleteButton = (subQuestId, progressionId) => {
    dispatch(subQuestReportActions.toggleDeleteProgressionModal(true));
    dispatch(
      subQuestReportActions.setDeleteProgressionValue({
        subQuestId,
        progressionId,
      })
    );
  };

  // Handle click event for viewing session details
  const handleClickViewSessionButton = (subQuestId, progressionId) => {
    dispatch(subQuestReportActions.toggleProgressionSessionsModal(true));
    dispatch(
      subQuestReportActions.setGetProgressionSessionsValue({
        subQuestId,
        progressionId,
      })
    );
  };

  // Handle click event for opening the file submission grading modal
  const handleClickOpenGradeButton = (
    courseId,
    questId,
    subQuestId,
    ownerId
  ) => {
    dispatch(getSubQuestById({ questId, subQuestId }));

    dispatch(
      getLatestFileSubmissionSession({
        courseId,
        questId,
        subQuestId,
        ownerId,
      })
    );

    dispatch(
      subquestActions.setUpdateFileSubmissionSession({
        modal: true,
      })
    );
  };

  // Return column configuration for the button
  return {
    name: 'Action',
    sortable: false,
    cell: (row) => (
      <Flex gap={2}>
        <Tooltip label="Remove Progression" fontSize="md">
          <Button
            colorScheme="gray"
            size="xs"
            onClick={() => handleClickDeleteButton(row.subQuestId, row.id)}
          >
            <CloseIcon />
          </Button>
        </Tooltip>

        {row.typeId === QUIZ_ID && (
          <Tooltip label="View Sessions" fontSize="md">
            <Button
              colorScheme="blue"
              size="xs"
              onClick={() =>
                handleClickViewSessionButton(row.subQuestId, row.id)
              }
            >
              <FaThList />
            </Button>
          </Tooltip>
        )}

        {row.typeId === INTERACTIVE_ID && row.interactiveContentScore && (
          <Tooltip label="View Sessions" fontSize="md">
            <Button
              colorScheme="blue"
              size="xs"
              onClick={() =>
                handleClickViewSessionButton(row.subQuestId, row.id)
              }
            >
              <FaThList />
            </Button>
          </Tooltip>
        )}

        {row.typeId === FILE_SUBMISSION_ID && (
          <Tooltip label="Grade Submission" fontSize="md">
            <Button
              colorScheme="teal"
              size="xs"
              onClick={() => {
                handleClickOpenGradeButton(
                  row.courseId,
                  row.questId,
                  row.subQuestId,
                  row.ownerId
                );
              }}
            >
              <MdAssignmentTurnedIn />
            </Button>
          </Tooltip>
        )}
      </Flex>
    ),
  };
};

// Function to determine the text color based on the result
export const getResultTextColor = (result) => {
  return resultTextColors[result] || resultTextColors.Default;
};

// Base columns shared across different sub-quest types
export const baseSubQuestColumns = [
  {
    name: 'Id', // Unique identifier for the row
    selector: (row) => row.id,
    sortable: true,
    sortField: 'id',
  },
  {
    name: 'Name', // Name of the owner
    selector: (row) => row.ownerName,
    sortable: true,
    cell: (row) => (
      <Text fontWeight={500} as="i">
        {row.ownerName}
      </Text>
    ),
    sortField: 'ownerName',
  },
  {
    name: 'Course', // Name of the course
    selector: (row) => row.courseName,
    sortable: true,
    cell: (row) => <Text>{row.courseName}</Text>,
    sortField: 'courseName',
  },
  {
    name: 'Quest', // Name of the quest
    selector: (row) => row.questName,
    sortable: true,
    cell: (row) => <Text>{row.questName}</Text>,
    sortField: 'questName',
  },
  {
    name: 'Date Started', // Date when the quest was started
    selector: (row) => row.dateStarted,
    sortable: true,
    cell: (row) => (
      <Text>
        {row.dateStarted
          ? format(parseJSON(row.dateStarted), 'MMMM d, yyyy (h:mm a)')
          : 'N/A'}
      </Text>
    ),
    sortField: 'dateStarted',
  },
  {
    name: 'Date Finished', // Date when the quest was finished
    selector: (row) => row.dateFinished,
    sortable: true,
    cell: (row) => (
      <Text>
        {row.dateFinished
          ? format(parseJSON(row.dateFinished), 'MMMM d, yyyy (h:mm a)')
          : 'N/A'}
      </Text>
    ),
    sortField: 'dateFinished',
  },
];

// Columns specific to video sub-quests
export const videoSubQuestColumns = [
  ...baseSubQuestColumns,
  {
    name: 'Status', // Status of the video sub-quest (e.g., Pending, Completed)
    selector: (row) => row.status,
    sortable: true,
    cell: (row) => (
      <Badge colorScheme={row.status === 'Pending' ? 'blue' : 'green'}>
        {row.status}
      </Badge>
    ),
    sortField: 'status',
  },
];

// Columns specific to quiz sub-quests
export const quizSubQuestColumns = [
  ...baseSubQuestColumns,
  {
    name: 'Score', // Quiz score and result (Pass/Fail)
    selector: (row) => row.quizScore,
    sortable: true,
    cell: (row) => (
      <Flex align="center" direction="column">
        <Text>{row.quizScore ? parseFloat(row.quizScore.toFixed(2)) : 0}</Text>
        <Text>
          {row.dateStarted && !row.dateFinished ? (
            <Badge colorScheme="blue">WIP</Badge>
          ) : (
            <Badge colorScheme={row.quizResult === 'Pass' ? 'green' : 'red'}>
              {row.quizResult || 'Fail'}
            </Badge>
          )}
        </Text>
      </Flex>
    ),
    sortField: 'quizScore',
  },
];

// Columns specific to interactive sub-quests
export const interactiveSubQuestColumns = [
  ...baseSubQuestColumns,
  {
    name: 'Status', // Status and score of the interactive sub-quest
    selector: (row) => row.status,
    sortable: true,
    cell: (row) => (
      <Flex align="center" direction="column">
        {row.interactiveContentScore && (
          <Text>
            Score: {parseFloat(row.interactiveContentScore.toFixed(2))}
          </Text>
        )}
        <Text>
          <Badge colorScheme={row.status === 'Pending' ? 'blue' : 'green'}>
            {row.status}
          </Badge>
        </Text>
      </Flex>
    ),
    sortField: 'status',
  },
];

// Columns specific to narrative sub-quests
export const narrativeSubQuestColumns = [
  ...baseSubQuestColumns,
  {
    name: 'Status', // Status of the narrative sub-quest (e.g., Pending, Completed)
    selector: (row) => row.status,
    sortable: true,
    cell: (row) => (
      <Badge colorScheme={row.status === 'Pending' ? 'blue' : 'green'}>
        {row.status}
      </Badge>
    ),
    sortField: 'status',
  },
];

export const fileSubmissionSubQuestColumns = [
  ...baseSubQuestColumns,
  {
    name: 'Score',
    selector: (row) => row.fileSubmissionScore,
    sortable: true,
    cell: (row) => (
      <Text>
        {row.fileSubmissionScore
          ? parseFloat(row.fileSubmissionScore.toFixed(2))
          : 0}
      </Text>
    ),
    sortField: 'fileSubmissionScore',
  },
  {
    name: 'Result',
    selector: (row) => row.fileSubmissionResult,
    sortable: true,
    cell: (row) => (
      <Flex align="center" direction="column">
        <Text>
          {row.dateStarted && !row.dateFinished ? (
            <Badge colorScheme="blue">WIP</Badge>
          ) : (
            <Badge colorScheme={getResultTextColor(row.fileSubmissionResult)}>
              {row.fileSubmissionResult || 'No Result'}
            </Badge>
          )}
        </Text>
      </Flex>
    ),
    sortField: 'fileSubmissionResult',
  },
];
